import { useEffect, useRef, useState } from 'preact/hooks'
import { Captions } from 'lucide-preact'
import type { WebinarRoomSubtitlesDto } from '@/api/webinarRoom'
import { SUBTITLE_STYLES, subtitleLanguageName } from './subtitles'
import type { SubtitleStyle } from './subtitles'
import { useSubtitlePreference } from './useSubtitlePreference'

interface SubtitleControlsProps {
    languageCode: string
    on: boolean
    onToggle: () => void
    style: SubtitleStyle
    onPickStyle: (style: SubtitleStyle) => void
    /** The class the controls beside these use, so the two buttons match them. */
    buttonClass?: string
    iconSize?: number
    /** Under controls drawn over the picture, over a bar drawn under it. */
    menu: 'below' | 'above'
}

/**
 * CC, and while subtitles are on, Aa beside it for the style menu: Dark, Light or Glass, each shown on a light and a
 * dark ground. The menu closes on Escape, which goes no further, and on a click anywhere else.
 */
export function SubtitleControls({ languageCode, on, onToggle, style, onPickStyle, buttonClass, iconSize = 18, menu }: SubtitleControlsProps) {
    const [open, setOpen] = useState(false)
    const box = useRef<HTMLSpanElement>(null)
    const styleButton = useRef<HTMLButtonElement>(null)
    const language = subtitleLanguageName(languageCode)
    const showMenu = on && open

    useEffect(() => {
        if (!showMenu) return
        const onPointerDown = (event: PointerEvent) => {
            if (!box.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', onPointerDown)
        return () => document.removeEventListener('pointerdown', onPointerDown)
    }, [showMenu])

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !showMenu) return
        event.stopPropagation()
        setOpen(false)
        styleButton.current?.focus()
    }
    const classOf = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ')

    return (
        <span class="v-subs-ctl" ref={box} onKeyDown={onKeyDown}>
            <button
                type="button"
                class={classOf(buttonClass, 'v-subs-cc', on && 'is-on')}
                aria-label="Subtitles"
                aria-pressed={on}
                title={language ? `Subtitles (${language})` : 'Subtitles'}
                onClick={() => { setOpen(false); onToggle() }}
            >
                <Captions size={iconSize} class="ic" aria-hidden="true" />
            </button>
            {on && (
                <button
                    type="button"
                    ref={styleButton}
                    class={classOf(buttonClass, 'v-subs-aa', open && 'is-open')}
                    aria-label="Subtitle style"
                    aria-expanded={open}
                    title="Subtitle style"
                    onClick={() => setOpen(!open)}
                >
                    Aa
                </button>
            )}
            {showMenu && (
                <div class={`v-subs-menu is-${menu}`} role="group" aria-label="Subtitle style">
                    <p class="v-subs-menu-h">Subtitle style</p>
                    <div class="v-subs-swatches">
                        {SUBTITLE_STYLES.map(option => (
                            <button key={option.id} type="button" class="v-subs-swatch" aria-pressed={option.id === style} onClick={() => onPickStyle(option.id)}>
                                <span class="v-subs-sample" aria-hidden="true"><span class={`v-subs-line is-${option.id}`}>Aa</span></span>
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </span>
    )
}

/**
 * The controls bound to this browser's choice, remembered for its next visit: every room player but the Room tab's
 * preview, whose CC is its own. Nothing without subtitles.
 */
export function RoomSubtitleControls({ subtitles, ...placement }: { subtitles: WebinarRoomSubtitlesDto | null | undefined } & Pick<SubtitleControlsProps, 'buttonClass' | 'iconSize' | 'menu'>) {
    const preference = useSubtitlePreference()
    if (!subtitles) return null
    return (
        <SubtitleControls
            languageCode={subtitles.LanguageCode}
            on={preference.on}
            onToggle={() => preference.setOn(!preference.on)}
            style={preference.style}
            onPickStyle={preference.setStyle}
            {...placement}
        />
    )
}
